import type { Reference } from '@/components/detail/ExploreTab'

/**
 * Audit item 11: the API surfaces non-URL strings inside `references` (e.g.
 * `cisaKevDueDate: "2022-05-03"`) when upstream feeds embed dates or other
 * scalar metadata under the same key. ExploreTab then renders every entry
 * as `<a href={value}>` which produces broken links pointing at relative
 * date paths. The defence lives at the normaliser — a single chokepoint
 * for both Vulnerability and ThreatActor renderers — so any caller of
 * `normaliseReferences` is guaranteed URL-shaped output.
 *
 * "URL-shaped" = `https?://` prefix only. mailto:/ftp:/relative paths are
 * dropped on purpose — the references list is for outbound CTI links and
 * those non-http schemes never appear in real upstream feeds.
 */
const HTTP_URL_REGEX = /^https?:\/\//i

function isHttpUrl(value: string): boolean {
  return HTTP_URL_REGEX.test(value)
}

/**
 * Normalise the heterogeneous `references` field on Vulnerability and ThreatActor
 * records into a consistent `Reference[]` shape for ExploreTab.
 *
 * Handles four observed shapes from the API:
 *   1. `string[]`                       — array of plain URL strings
 *   2. `Record<string, string>`         — object with string values (URLs)
 *   3. `Array<{ url, source_name?... }>` — array of structured reference objects
 *   4. `Record<string, { url, ... }>`   — object with structured-object values
 *
 * Items with no usable URL are dropped (audit item 11); label falls back to
 * the URL itself. Non-URL-shaped strings (e.g. ISO dates) are filtered out
 * before they reach the renderer.
 */
export function normaliseReferences(raw: unknown): Reference[] {
  if (raw == null) return []

  const items: unknown[] = Array.isArray(raw)
    ? raw
    : typeof raw === 'object'
      ? Object.values(raw as Record<string, unknown>)
      : []

  const out: Reference[] = []
  for (const item of items) {
    if (typeof item === 'string') {
      const url = item.trim()
      if (!url) continue
      // Audit item 11: gate on URL shape so non-URL strings (e.g. dates,
      // free-text metadata) don't render as broken `<a>` tags.
      if (!isHttpUrl(url)) continue
      out.push({ url, label: url })
      continue
    }
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>
      const url = String(obj.url ?? obj.href ?? obj.link ?? '').trim()
      if (!url) continue
      // Same URL-shape gate for the structured-object branch.
      if (!isHttpUrl(url)) continue
      const label = String(
        obj.source_name ?? obj.label ?? obj.name ?? obj.title ?? url,
      ).trim()
      out.push({ url, label: label || url })
    }
  }
  return out
}
